'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { withLayout } from '../../shared/Styles.js'
import { Text, Heading, Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/index.js'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import type { ButtonFunctionType, CopyConfig } from './types.js'
import {
  ProductCard,
  type ProductCardData,
  type ActiveShelfSemanticName,
  type StockDisplayMode,
} from './ProductCard.js'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'shelf'
const componentName = 'active_shelf'

/**
 * 组件布局模式
 */
export type ActiveShelfLayoutMode = 'flex' | 'horizontal'

/**
 * Code Channel 配置 - 折扣前缀
 */
export type CodeChannelConfig = string

const PrevIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-info-primary laptop:block lg-desktop:size-[56px] hidden size-[40px]"
  >
    <path
      d="M0 28C0 43.464 12.536 56 28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28Z"
      fill="currentColor"
      fillOpacity={disabled ? '0.2' : '0.6'}
    />
    <path d="M31 22L25 28L31 34" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const NextIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-info-primary laptop:block lg-desktop:size-[56px] hidden size-[40px]"
  >
    <path
      d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z"
      fill="currentColor"
      fillOpacity={disabled ? '0.2' : '0.6'}
    />
    <path d="M25 22L31 28L25 34" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * 产品分组Tab数据接口
 */
export interface ProductTab {
  /** Tab ID */
  id: string
  /** Tab 标题 */
  tab: string
  /** 该分组下的产品数据 */
  data: ProductCardData[]
}

/**
 * ActiveShelf 业务组件数据接口
 */
export interface ActiveShelfData {
  theme?: 'light' | 'dark'
  /** 组件标题 */
  title?: string
  /** 组件描述 */
  description?: string
  /** 分组产品Tab列表 */
  productsTab?: ProductTab[]
  /** 是否显示 Tab（默认：true） */
  isShowTab?: boolean
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  secondaryButtonFun: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  primaryButtonFun: ButtonFunctionType
  /** 布局模式：网格布局或横向滑动 */
  layoutMode?: ActiveShelfLayoutMode
  /** Code Channel 配置 - 折扣前缀 */
  codeChannel?: CodeChannelConfig
  /** 是否展示会员价格 */
  showMemberPrice?: boolean
  /** 是否展示折扣标签 */
  showDiscount?: boolean
  /** 是否展示标签系统 */
  showTags?: boolean
  /** 库存展示模式 */
  stockDisplayMode?: StockDisplayMode
  /** 库存阈值（当模式为 below-quantity 或 below-percentage 时使用） */
  stockThresholdValue?: number
  /**
   * 库存文本模板，支持 {count} 占位符
   */
  stockDisplayText?: string
  /** 是否展示原价（删除线价格） */
  showOriginalPrice?: boolean
  /** 文案配置 */
  copy?: CopyConfig
  /**
   * 产品列表（当不使用 Tab 分组时可直接传入产品列表）
   */
  productsCard: ProductCardData[]
}

export interface ActiveShelfProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: ActiveShelfData
  /** 点击了解更多回调 */
  onLearnMore?: (product: ProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: ProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: ProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<ActiveShelfSemanticName, string>>
}

/**
 * ActiveShelf 复合组件类型定义
 */
export interface ActiveShelfComponent extends React.ForwardRefExoticComponent<
  ActiveShelfProps & React.RefAttributes<HTMLDivElement>
> {
  /** 独立的 ProductCard 组件 */
  ProductCard: typeof ProductCard
}

/**
 * 产品列表组件 Props
 */
interface ProductsListProps {
  products: ProductCardData[]
  layoutMode: ActiveShelfLayoutMode
  className?: string
  copy?: CopyConfig
  showTags?: boolean
  stockDisplayMode?: StockDisplayMode
  stockThresholdValue?: number
  onLearnMore?: (product: ProductCardData) => void
  onShopNow?: (product: ProductCardData) => void
  onAddToCart?: (product: ProductCardData) => void
  classNames?: Partial<Record<ActiveShelfSemanticName, string>>
  secondaryButtonText?: string
  secondaryButtonFun?: ButtonFunctionType
  primaryButtonText?: string
  primaryButtonFun?: ButtonFunctionType
  showOriginalPrice?: boolean
}

/**
 * 产品列表组件 - 支持网格和横向滑动两种布局
 */
const ProductsList = React.forwardRef<HTMLDivElement, ProductsListProps>(
  (
    {
      products,
      layoutMode,
      className,
      copy,
      showTags,
      stockDisplayMode,
      stockThresholdValue,
      onLearnMore,
      onShopNow,
      onAddToCart,
      classNames,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      showOriginalPrice,
    },
    ref
  ) => {
    // 生成唯一 ID，移除特殊字符以确保可用于 CSS 选择器
    const id = React.useId().replace(/:/g, '')
    // 预定义导航按钮选择器，避免多次拼接
    const nextButtonClass = `swiper-button-next-custom-${id}`
    const prevButtonClass = `swiper-button-prev-custom-${id}`

    // Swiper 导航状态
    const [swiperState, setSwiperState] = React.useState({
      isBeginning: true,
      isEnd: false,
    })

    // Swiper 实例引用
    const swiperRef = React.useRef<any>(null)

    // 计算并设置所有 slide 为最高高度
    const equalizeSlideHeights = React.useCallback(() => {
      const swiper = swiperRef.current
      if (!swiper?.slides || swiper.slides.length === 0) return

      // 先重置所有 slide 高度以获取真实内容高度
      swiper.slides.forEach((slide: HTMLElement) => {
        slide.style.height = 'auto'
      })

      // 计算最大高度
      // @ts-ignore
      const heights = Array.from(swiper.slides).map((slide: HTMLElement) => slide.scrollHeight)
      const maxHeight = Math.max(...heights)

      // 设置所有 slide 为最大高度
      if (maxHeight > 0) {
        swiper.slides.forEach((slide: HTMLElement) => {
          slide.style.height = `${maxHeight}px`
        })
      }
    }, [])

    // 监听窗口 resize 重新计算高度
    React.useEffect(() => {
      const handleResize = () => {
        // 使用 requestAnimationFrame 确保 DOM 更新完成
        requestAnimationFrame(equalizeSlideHeights)
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [equalizeSlideHeights])

    if (products?.length === 0) {
      return null
    }

    return (
      <div ref={ref} className={className}>
        {layoutMode === 'flex' ? (
          /* 网格布局 */
          <div className="lg-desktop:gap-4 tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 laptop:gap-4 grid grid-cols-1 justify-start gap-3">
            {products.map((product, index) => (
              <ProductCard
                key={product.shopify_id + '-' + product.sku || index}
                product={product}
                className="shrink-0"
                showTags={showTags}
                stockDisplayMode={stockDisplayMode}
                stockThresholdValue={stockThresholdValue}
                onLearnMore={onLearnMore}
                onShopNow={onShopNow}
                onAddToCart={onAddToCart}
                classNames={classNames}
                secondaryButtonText={secondaryButtonText}
                secondaryButtonFun={secondaryButtonFun}
                primaryButtonText={primaryButtonText}
                primaryButtonFun={primaryButtonFun}
                showOriginalPrice={showOriginalPrice}
                copy={copy}
              />
            ))}
          </div>
        ) : (
          /* Swiper 横向滑动布局 */
          <div className="group relative">
            <Swiper
              modules={[Navigation]}
              spaceBetween={16}
              slidesPerView="auto"
              navigation={{
                nextEl: `.${nextButtonClass}`,
                prevEl: `.${prevButtonClass}`,
              }}
              onSwiper={swiper => {
                swiperRef.current = swiper
                setSwiperState({
                  isBeginning: swiper.isBeginning,
                  isEnd: swiper.isEnd,
                })
                // 延迟执行以确保内容渲染完成
                setTimeout(equalizeSlideHeights, 100)
              }}
              onReachEnd={() => {
                setSwiperState(prev => ({ ...prev, isEnd: true }))
              }}
              onReachBeginning={() => {
                setSwiperState(prev => ({ ...prev, isBeginning: true }))
              }}
              onFromEdge={() => {
                setSwiperState({ isBeginning: false, isEnd: false })
              }}
              className="!overflow-visible"
            >
              {products.map((product, index) => (
                <SwiperSlide
                  key={product.shopify_id + '-' + product.sku || index}
                  className="tablet:!w-[288px] desktop:!w-[316px] lg-desktop:!w-[404px] !w-[296px] overflow-hidden"
                >
                  <ProductCard
                    product={product}
                    className="w-full max-w-full"
                    showTags={showTags}
                    stockDisplayMode={stockDisplayMode}
                    stockThresholdValue={stockThresholdValue}
                    onLearnMore={onLearnMore}
                    onShopNow={onShopNow}
                    onAddToCart={onAddToCart}
                    classNames={classNames}
                    secondaryButtonText={secondaryButtonText}
                    secondaryButtonFun={secondaryButtonFun}
                    primaryButtonText={primaryButtonText}
                    primaryButtonFun={primaryButtonFun}
                    showOriginalPrice={showOriginalPrice}
                    copy={copy}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* 自定义导航按钮 - 默认隐藏，hover 时显示，disabled 时隐藏 */}
            <button
              className={`${prevButtonClass} tablet:block absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 transition-opacity ${swiperState.isBeginning ? 'pointer-events-none opacity-0' : 'opacity-0 group-hover:opacity-100'}`}
              aria-label={copy?.previousSlideLabel}
            >
              <PrevIcon disabled={swiperState.isBeginning} />
            </button>
            <button
              className={`${nextButtonClass} tablet:block absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 transition-opacity ${swiperState.isEnd ? 'pointer-events-none opacity-0' : 'opacity-0 group-hover:opacity-100'}`}
              aria-label={copy?.nextSlideLabel}
            >
              <NextIcon disabled={swiperState.isEnd} />
            </button>
          </div>
        )}
      </div>
    )
  }
)

ProductsList.displayName = 'ProductsList'

/**
 * ActiveShelf - 活动货架
 *
 * @description 能够实时显示商品库存信息和会员价的货架，支持响应式布局和多种配置选项
 */
const ActiveShelf = React.forwardRef<HTMLDivElement, ActiveShelfProps>(
  ({ className, data, onLearnMore, onShopNow, onAddToCart, classNames, ...props }, ref) => {
    const boxRef = React.useRef<HTMLDivElement>(null)

    useExposure(boxRef, {
      componentType,
      componentName,
      componentTitle: data.title,
      componentDescription: data.description,
    })

    React.useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

    // 从 data 中解构配置项，并设置默认值
    const {
      theme = 'light',
      layoutMode = 'flex',
      showTags = true,
      stockDisplayMode,
      stockThresholdValue,
      showOriginalPrice = true,
      secondaryButtonText,
      primaryButtonText,
      secondaryButtonFun,
      primaryButtonFun,
      isShowTab = true,
      copy,
    } = data

    // 构建 copy 配置，使用传入的 copy 配置
    const finalCopy: CopyConfig = copy || {}

    // 获取要显示的产品数据
    const hasTabData = data.productsTab && data.productsTab.length > 0
    const shouldShowTab = isShowTab && hasTabData

    return (
      <div
        ref={boxRef}
        className={cn(
          'w-full',
          {
            'aiui-dark': theme === 'dark',
          },
          className,
          classNames?.root
        )}
        style={
          {
            '--progress-track-bg': theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
          } as React.CSSProperties
        }
        {...props}
      >
        {/* 标题区域 */}
        {data.title && (
          <Heading as="h2" html={data.title} size={4} className={cn('text-info-primary mb-4', classNames?.title)} />
        )}
        {data.description && (
          <Text
            size={3}
            as="p"
            html={data.description}
            className={cn('text-info-primary mb-6', classNames?.description)}
          />
        )}

        {/* 产品展示区域 */}

        <Tabs defaultValue={data.productsTab?.[0]?.id} className="w-full">
          {shouldShowTab && (
            <TabsList className="mb-6">
              {data.productsTab?.map(tab => (
                <TabsTrigger key={'tab-' + tab.id} value={tab.id}>
                  {tab.tab}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
          {shouldShowTab ? (
            data.productsTab?.map(tab => (
              <TabsContent key={'tab-content-' + tab.id} value={tab.id}>
                <ProductsList
                  products={tab.data}
                  layoutMode={layoutMode}
                  copy={finalCopy}
                  showTags={showTags}
                  stockDisplayMode={stockDisplayMode}
                  stockThresholdValue={stockThresholdValue}
                  onLearnMore={onLearnMore}
                  onShopNow={onShopNow}
                  onAddToCart={onAddToCart}
                  classNames={classNames}
                  secondaryButtonText={secondaryButtonText}
                  secondaryButtonFun={secondaryButtonFun}
                  primaryButtonText={primaryButtonText}
                  primaryButtonFun={primaryButtonFun}
                  showOriginalPrice={showOriginalPrice}
                />
              </TabsContent>
            ))
          ) : (
            <ProductsList
              products={data.productsCard}
              layoutMode={layoutMode}
              copy={finalCopy}
              showTags={showTags}
              stockDisplayMode={stockDisplayMode}
              stockThresholdValue={stockThresholdValue}
              onLearnMore={onLearnMore}
              onShopNow={onShopNow}
              onAddToCart={onAddToCart}
              classNames={classNames}
              secondaryButtonText={secondaryButtonText}
              secondaryButtonFun={secondaryButtonFun}
              primaryButtonText={primaryButtonText}
              primaryButtonFun={primaryButtonFun}
              showOriginalPrice={showOriginalPrice}
            />
          )}
        </Tabs>
      </div>
    )
  }
)

ActiveShelf.displayName = 'ActiveShelf'

// 创建复合组件
const ActiveShelfWithProductCard = ActiveShelf as ActiveShelfComponent
ActiveShelfWithProductCard.ProductCard = ProductCard

const ActiveShelfWrapped = withLayout(ActiveShelfWithProductCard)

export default ActiveShelfWrapped
export type { StockDisplayMode, ActiveShelfSemanticName, ProductCardData }
